"use client";

import React, { useState, useEffect } from "react";

type Props = {
    appraisedValue: number;
    setAppraisedValue: (value: number) => void;
    expectedYears: number;
    setExpectedYears: (value: number) => void;
};

const formatWithCommas = (value: number | string | undefined): string =>
    typeof value === "number" || typeof value === "string"
        ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : "";

const HomeInfo = ({ appraisedValue, setAppraisedValue, expectedYears, setExpectedYears }: Props) => {
    const [appraisedInput, setAppraisedInput] = useState("0");
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setAppraisedInput(formatWithCommas(appraisedValue));
    }, [appraisedValue]);

    return (
        <div className="border border-gray-300 overflow-hidden shadow 2xl:w-[900px]">
            <button
                type="button"
                className="w-full text-left bg-[#202358] text-white px-4 py-3 cursor-pointer select-none flex items-center justify-between hover:bg-blue active:bg-blue focus:bg-blue 2xl:h-[42px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="text-[18px] font-bold uppercase">Home Info</span>
                <span className="text-xl">{isOpen ? "−" : "+"}</span>
            </button>

            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="bg-white p-2 2xl:w-[898px]">
                    {/* Appraised Value */}
                    <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                        <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                            <label
                                htmlFor="appraisedValue"
                                className="w-32 font-medium text-black text-[15px] 2xl:w-[125px]"
                            >
                                Appraised Value: $
                            </label>
                            <input
                                id="appraisedValue"
                                type="text"
                                inputMode="numeric"
                                className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                value={appraisedInput}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/,/g, "");
                                    if (/^\d*$/.test(raw)) {
                                        const numeric = Number(raw);
                                        setAppraisedValue(numeric);
                                        setAppraisedInput(formatWithCommas(raw));
                                    }
                                }}
                                onBlur={() => setAppraisedInput(formatWithCommas(appraisedValue))}
                            />
                        </div>
                        <input
                            aria-label="Appraised value"
                            type="range"
                            min={0}
                            max={10000000}
                            step={1000}
                            value={appraisedValue}
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                setAppraisedValue(val);
                                setAppraisedInput(formatWithCommas(val));
                            }}
                            className="w-full 2xl:w-[506px] accent-blue"
                        />
                    </div>

                    <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                        <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                            <span>$0</span>
                            <span>$100K</span>
                            <span>$1M</span>
                            <span>$10M</span>
                        </div>
                    </div>

                    {/* Expected Years in Home */}
                    <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                        <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                            <label
                                htmlFor="expectedYears"
                                className="w-32 font-medium text-black text-[15px] 2xl:w-[165px]"
                            >
                                Expected Years in Home:
                            </label>
                            <input
                                id="expectedYears"
                                type="number"
                                className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                value={expectedYears}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    const clamped = Math.min(Math.max(val, 1), 40);
                                    setExpectedYears(clamped);
                                }}
                            />
                        </div>
                        <input
                            aria-label="Expected years in home"
                            type="range"
                            min={1}
                            max={40}
                            step={1}
                            value={expectedYears}
                            onChange={(e) => setExpectedYears(Number(e.target.value))}
                            className="w-full 2xl:w-[506px] accent-blue"
                        />
                    </div>

                    <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                        <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                            <span>1</span>
                            <span>13</span>
                            <span>26</span>
                            <span>40</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomeInfo;
